import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, AlertTriangle, Ban, Clock, FileSignature, Users, Eye, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const STEPS = [
  {
    eyebrow: "STEP 01 / 05",
    title: "The scenario",
    sub: "What has gone wrong on the corridor.",
    body: (
      <div className="space-y-4 text-[15px] leading-relaxed">
        <p>A storm has narrowed the tidal window into Greenport. <strong>MV Northern Wave</strong> reports an ETA slip of ~4 hours. The terminal yard is already at 92% capacity.</p>
        <p>Two priority containers are at risk of missing their onward commitments — including <strong>MSCU 779431-2</strong> (temperature-controlled pharma, regulated cut-off in 90 minutes). A third container, <strong>HLBU 220987-5</strong>, is under active compliance review.</p>
        <p>This is the moment where the response either fragments across systems and inboxes, or runs through a single governed flow.</p>
      </div>
    ),
    side: { icon: AlertTriangle, label: "Multi-party disruption", value: "4h ETA slip · 92% yard · 1 doc hold · 1 pharma cut-off" }
  },
  {
    eyebrow: "STEP 02 / 05",
    title: "The shared operational picture",
    sub: "Each role sees what they need — not everything everywhere.",
    body: (
      <div className="space-y-4 text-[15px] leading-relaxed">
        <p>FreightCierge does not centralise raw operational data. It exposes a <strong>purpose-bound view</strong> per role.</p>
        <ul className="space-y-2 text-[14px]">
          <li>· <strong>Vessel Ops</strong>: berth window, restow implications</li>
          <li>· <strong>Terminal Yard</strong>: slot allocation, congestion threshold</li>
          <li>· <strong>Haulage</strong>: gate-out windows, container readiness</li>
          <li>· <strong>Compliance</strong>: doc holds, named release queue</li>
          <li>· <strong>Port Ops &amp; Escalation</strong>: corridor view, shipper notifications</li>
        </ul>
      </div>
    ),
    side: { icon: Eye, label: "Purpose-bound visibility", value: "6 roles · 6 different views of the same event" }
  },
  {
    eyebrow: "STEP 03 / 05",
    title: "The KATLAS governance gate",
    sub: "CAR rules — Custody, Authority, Receipts — applied at the point of action.",
    body: (
      <div className="space-y-4 text-[15px] leading-relaxed">
        <p>When a candidate action is selected, the KATLAS node evaluates it at runtime:</p>
        <div className="grid sm:grid-cols-3 gap-3 text-[13px]">
          <div className="border border-[var(--line)] p-4">
            <div className="mono uppercase text-[11px] text-[var(--ink-soft)] tracking-wider mb-1">Custody</div>
            <p>Who currently holds responsibility or control over this asset?</p>
          </div>
          <div className="border border-[var(--line)] p-4">
            <div className="mono uppercase text-[11px] text-[var(--ink-soft)] tracking-wider mb-1">Authority</div>
            <p>Who is permitted to decide or act under these conditions?</p>
          </div>
          <div className="border border-[var(--line)] p-4">
            <div className="mono uppercase text-[11px] text-[var(--ink-soft)] tracking-wider mb-1">Receipts</div>
            <p>What signed evidence will the action produce?</p>
          </div>
        </div>
        <p className="text-[14px] text-[var(--ink-soft)]">AI or predictive models may recommend. They do not determine authority.</p>
      </div>
    ),
    side: { icon: ShieldCheck, label: "Runtime authority check", value: "Identity · role · org · context · policy" }
  },
  {
    eyebrow: "STEP 04 / 05",
    title: "Coordinated action",
    sub: "Three outcomes — and the demo will show all three.",
    body: (
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="border border-[var(--line)] p-4">
          <span className="fc-chip fc-chip-allowed mb-3"><Check size={12}/> Allowed</span>
          <div className="text-[13px] mt-2">Terminal Yard reslots a non-priority container — within threshold, no doc hold.</div>
        </div>
        <div className="border border-[var(--line)] p-4">
          <span className="fc-chip fc-chip-blocked mb-3"><Ban size={12}/> Blocked</span>
          <div className="text-[13px] mt-2">Haulage attempts to gate-out a container under compliance hold — no decision rights.</div>
        </div>
        <div className="border border-[var(--line)] p-4">
          <span className="fc-chip fc-chip-escalated mb-3"><Clock size={12}/> Escalated</span>
          <div className="text-[13px] mt-2">Port Ops proposes priority handling for regulated pharma — requires Compliance co-sign.</div>
        </div>
      </div>
    ),
    side: { icon: Users, label: "Multi-authority coordination", value: "Auto · denied · co-signed" }
  },
  {
    eyebrow: "STEP 05 / 05",
    title: "The evidence pack",
    sub: "Receipts assemble themselves as the response unfolds.",
    body: (
      <div className="space-y-4 text-[15px] leading-relaxed">
        <p>Every consequential action produces a receipt with: actor, role, organisation, authority used, decision basis, linked upstream event, downstream notifications, and any approvals or denials.</p>
        <p>The evidence pack is assembled in real time — exportable for regulators, customers, internal audit, or insurers.</p>
        <p className="text-[14px] text-[var(--ink-soft)]">In the live sandbox you can export it as JSON.</p>
      </div>
    ),
    side: { icon: FileSignature, label: "Attributable receipts", value: "Signed · linked · exportable" }
  },
];

export default function Walkthrough() {
  const [i, setI] = useState(0);
  const s = STEPS[i];
  const Side = s.side.icon;

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-12">
      {/* Progress */}
      <div className="flex items-center gap-3 mb-10" data-testid="walkthrough-progress">
        {STEPS.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setI(idx)}
            data-testid={`step-dot-${idx}`}
            className={`flex-1 h-1.5 rounded-sm transition-colors ${
              idx <= i ? "bg-[var(--navy)]" : "bg-[var(--line)]"
            }`}
            aria-label={`Go to step ${idx+1}`}
          />
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-10 fc-fade-up" key={i}>
        <div className="lg:col-span-8">
          <div className="fc-eyebrow mb-4">{s.eyebrow}</div>
          <h1 className="display text-[40px] leading-[1] mb-3" data-testid="step-title">{s.title}</h1>
          <p className="text-[var(--ink-soft)] text-[17px] mb-8">{s.sub}</p>
          <div className="fc-card p-7">{s.body}</div>

          <div className="flex items-center justify-between mt-8">
            <Button
              variant="ghost"
              disabled={i === 0}
              onClick={() => setI(i - 1)}
              data-testid="prev-btn"
              className="rounded-sm h-11"
            >
              <ArrowLeft size={16} className="mr-2"/> Previous
            </Button>
            {i < STEPS.length - 1 ? (
              <Button onClick={() => setI(i + 1)} className="bg-[var(--navy)] hover:bg-black rounded-sm h-11" data-testid="next-btn">
                Next <ArrowRight size={16} className="ml-2"/>
              </Button>
            ) : (
              <Button asChild className="bg-[var(--navy)] hover:bg-black rounded-sm h-11" data-testid="open-sandbox-btn">
                <Link to="/sandbox">Open the live sandbox <ArrowRight size={16} className="ml-2"/></Link>
              </Button>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="fc-card p-6 sticky top-24">
            <Side size={22} className="text-[var(--teal)] mb-4" strokeWidth={1.6}/>
            <div className="fc-eyebrow mb-2">{s.side.label}</div>
            <div className="display text-[18px] leading-snug">{s.side.value}</div>
            <div className="fc-section-rule pt-4 mt-5">
              <div className="mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-2">Avoid</div>
              <p className="text-[12px] text-[var(--ink-soft)]">"single source of truth" · "tamperproof ledger" · "replace existing systems"</p>
              <div className="mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mt-3 mb-2">Prefer</div>
              <p className="text-[12px] text-[var(--ink-soft)]">governed workflow · decision rights · runtime authority · attributable receipts</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
